// "Remove" on an orphan BLE record — a DeviceID 100 device with no lamp group
// behind it (core/api/bleNodes.removeOrphanBleRecord). Right on the card or
// screen that shows it, because there is nothing else to open: the group
// screens need the group that is missing.
//
// Offered to a Super Admin or the record's owner; everyone else sees only the
// explanation.

import React, { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApp } from '../../../core/state/AppContext';
import { removeOrphanBleRecord } from '../../../core/api/bleNodes';
import { getDeviceDetail } from '../../../core/api/api';
import { FIT_XS } from '../fitDialog';

export default function OrphanBleRemove({ device, onRemoved, size = 'small' }) {
  const { userId, isSuperAdmin } = useApp();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Room rows carry no MasterUserId: ask the device row who owns it.
  const [owner, setOwner] = useState(device?.MasterUserId ?? null);
  useEffect(() => {
    if (isSuperAdmin || owner != null || !device?.UniqueNumber) return undefined;
    let alive = true;
    getDeviceDetail(device.UniqueNumber).then((res) => {
      const rows = Array.isArray(res?.data) ? res.data : [];
      if (alive) setOwner(rows[0]?.MasterUserId ?? 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [isSuperAdmin, owner, device?.UniqueNumber]);
  if (!isSuperAdmin && Number(owner) !== Number(userId)) return null;

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await removeOrphanBleRecord(device, userId);
      setOpen(false);
      onRemoved?.();
    } catch (e) {
      setError(e?.message || 'Could not remove it');
    } finally {
      setBusy(false);
    }
  };
  const name = device.DeviceName || device.UniqueNumber;

  return (
    <>
      <Button size={size} color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        Remove
      </Button>
      <Dialog open={open} onClose={busy ? undefined : () => setOpen(false)} {...FIT_XS} onClick={(e) => e.stopPropagation()}>
        <DialogTitle>{`Remove ${name}?`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            It is an old Bluetooth record with no lamps behind it. It disappears from every room and
            every user it was shared with.
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={remove} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
